import { removeGid } from './handleGid';

// Type definitions
type ShopifyWindow = Window & {
  Shopify?: {
    routes?: {
      root?: string;
    };
  };
  freeGift?: {
    refetch?: () => void;
  };
};

type AjaxCartItem = {
  key: string;
  properties?: Record<string, any>;
};

type AjaxCart = {
  items?: AjaxCartItem[];
};

type CartDrawerResponse = {
  sections?: {
    'cart-drawer'?: string;
  };
};

type ChangeGiftOptions = {
  newGiftVariantId: string;
  vendor?: string | null;
  properties?: Record<string, any>;  
}

type ChooseGiftOptions = {
  variantId: string;
  vendor?: string | null;
  properties?: Record<string, any>;
  remove?: boolean;
};

export type RemoveGiftOptions = {
  skipDrawerFetch?: boolean;
};

export type ChooseGiftResult = {
  ok: boolean;
  cartDrawer?: string;
  errors?: Array<{ message: string }>;
};

export type RemoveGiftResult = {
  ok: boolean;
  removed: number;
  cartDrawer?: string;
  errors?: Array<{ message: string }>;
};

/**
 * Changes a free gift by removing any existing free gift and then adding a new one.
 * Performs operations sequentially without refreshing the drawer during transition.
 * Only the final add operation returns the cart drawer HTML.
 */
export async function changeGift({ 
  newGiftVariantId, 
  vendor, 
  properties = {},

}: ChangeGiftOptions): Promise<ChooseGiftResult> {
  
  if (typeof newGiftVariantId !== 'string' || newGiftVariantId.length === 0) {
    console.warn('[GWP] changeGift aborted: missing newGiftVariantId');
    return { ok: false, errors: [{ message: 'Missing variant id.' }] };
  }

  try {
    // Step 1: Remove existing gift without requesting cart drawer HTML
    const removeResult = await removeGift({ skipDrawerFetch: true });

    // Warn if remove failed, but continue to add new gift
    if (!removeResult.ok) {
      console.warn('[GWP] changeGift: failed to remove old gift, continuing with add');
    }

    // Step 2: Add new gift with cart drawer HTML in response
    const chooseResult = await chooseGift({ 
      variantId: newGiftVariantId, 
      vendor, 
      properties, 
      remove: false // Already removed above
    });

    // Check if add operation succeeded (most critical)
    if (!chooseResult.ok) {
      console.error('[GWP] changeGift: failed to add new gift');
      return chooseResult;
    }

    return chooseResult;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('[GWP] changeGift error', error);
    return { ok: false, errors: [{ message }] };
  }
}

/**
 * Adds a free gift line to the cart using AJAX API with the _freeGift property.
 * Removes any existing free gift first.
 */
export async function chooseGift({ 
  variantId, 
  vendor, 
  properties = {},
  remove = true
}: ChooseGiftOptions): Promise<ChooseGiftResult> {
  const base = (window as ShopifyWindow)?.Shopify?.routes?.root || '/';
  
  if (typeof variantId !== 'string' || variantId.length === 0) {
    console.warn('[GWP] chooseGift aborted: missing variantId');
    return { ok: false, errors: [{ message: 'Missing variant id.' }] };
  }

  // Ensure only one gift exists: remove any current gift first.
  if(remove){
    await removeGift();
  }

  const numericVariantId = toAjaxVariantId(variantId);
  const addUrl = base + 'cart/add.js?gwp=true';
  const body = {
    items: [
      {
        id: numericVariantId,
        quantity: 1,
        properties
      }
    ],
    sections: 'cart-drawer'
  };

  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      const message = `Failed to add gift (status ${res.status})`;
      console.error('[GWP] chooseGift failed', message);
      return { ok: false, errors: [{ message }] };
    }

    const cartResponse: CartDrawerResponse = await res.json();
    const cartDrawer = cartResponse.sections?.['cart-drawer'];

    return { ok: true, cartDrawer };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('[GWP] chooseGift error', error);
    return { ok: false, errors: [{ message }] };
  }
}

/**
 * Removes any free gift line(s) from the cart using AJAX API.
 * @param options.skipDrawerFetch - If true, skips fetching cart drawer HTML (used during gift changes to avoid intermediate refreshes)
 */
export async function removeGift(options: RemoveGiftOptions = {}): Promise<RemoveGiftResult> {
  const { skipDrawerFetch = false } = options;
  const base = (window as ShopifyWindow)?.Shopify?.routes?.root || '/';
  const cartUrl = base + 'cart.js?gwp=true';
  
  try {
    // Fetch current cart to locate free gift line keys.
    const cartRes = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    
    if (!cartRes.ok) {
      console.error('[GWP] removeGift failed to load cart', cartRes.status);
      return { ok: false, removed: 0, errors: [{ message: 'Failed to load cart.' }] };
    }
    
    const cart: AjaxCart = await cartRes.json();
    const keys = findFreeGiftItemKeys(cart);
    
    if (keys.length === 0) {
      // Only fetch drawer HTML if not skipping
      const cartDrawer = skipDrawerFetch ? undefined : await fetchCartDrawerHtml(base);
      return { ok: true, removed: 0, cartDrawer: cartDrawer || undefined };
    }

    // Remove each gift line sequentially to avoid race conditions.
    const changeUrl = base + 'cart/change.js?gwp=true';
    const outcomes: boolean[] = [];
    let latestCartDrawer: string | null = null;
    
    for (const key of keys) {
      try {
        // Only request cart-drawer section if not skipping
        const body = skipDrawerFetch 
          ? { id: key, quantity: 0 }
          : { id: key, quantity: 0, sections: 'cart-drawer' };
        
        const res = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body)
        });
        
        if (!res.ok) {
          outcomes.push(false);
          continue;
        }
        
        // Only capture drawer HTML if not skipping
        if (!skipDrawerFetch) {
          const data: CartDrawerResponse = await res.json();
          latestCartDrawer = data?.sections?.['cart-drawer'] || latestCartDrawer;
        }
        
        outcomes.push(true);
      } catch (err) {
        console.warn('[GWP] removeGift change error', err);
        outcomes.push(false);
      }
    }

    const allOk = outcomes.every(Boolean);
    (window as ShopifyWindow).freeGift?.refetch?.();
    
    return {
      ok: allOk,
      removed: keys.length,
      cartDrawer: latestCartDrawer || undefined,
      errors: allOk ? undefined : [{ message: 'Failed to remove one or more gift lines.' }]
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('[GWP] removeGift error', error);
    return { 
      ok: false, 
      removed: 0, 
      errors: [{ message }] 
    };
  }
}

/**
 * Checks if a line item has a specific property.
 */
function hasLineItemProperty(propertiesObject: Record<string, any> | undefined, key: string): boolean {
  if (!propertiesObject) return false;
  return typeof propertiesObject[`${key}`] !== 'undefined';
}

/**
 * Returns keys for items tagged as _freeGift or _promotional in the AJAX cart.
 */
export function findFreeGiftItemKeys(ajaxCart: AjaxCart): string[] {
  const items = Array.isArray(ajaxCart?.items) ? ajaxCart.items : [];
  return items
    .filter(
      (i) =>
        i?.properties &&
        (hasLineItemProperty(i?.properties, '_freeGift') || hasLineItemProperty(i?.properties, '_promotional'))
    )
    .map((i) => String(i.key))
    .filter(Boolean);
}

/**
 * Converts a GID variant id to a numeric AJAX variant id string.
 */
function toAjaxVariantId(gid: string): string {
  try {
    const id = removeGid({ id: gid });
    return id;
  } catch (e) {
    console.warn('[GWP] Failed to parse GID, using raw id', gid, e);
    // If parsing fails, fallback to given id and hope it's numeric.
    return gid;
  }
}

/**
 * Fetches the cart drawer HTML by requesting a section render from the update API.
 */
async function fetchCartDrawerHtml(base: string): Promise<string | null> {
  const updateUrl = base + 'cart/update.js?gwp=true';
  
  try {
    const res = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: {}, sections: 'cart-drawer' })
    });
    
    if (!res.ok) return null;
    
    const data: CartDrawerResponse = await res.json();
    return data?.sections?.['cart-drawer'] || null;
  } catch (e) {
    console.warn('[GWP] fetchCartDrawerHtml failed', e);
    return null;
  }
}